import React, { useEffect, useState } from "react";
import api from "../../../api/axios";

/* ------------------------------------------------------------
   FORMATTER → Indian Rupee with commas (15,833.33 INR)
-------------------------------------------------------------*/
function formatINR(amount) {
  if (amount === undefined || amount === null) return "0 INR";

  return (
    Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " INR"
  );
}

export default function PayrollDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);

    try {
      const res = await api.get("hrms/prms/dashboard");
      setData(res.data.data); // your JSON → { success: true, data: {...} }
    } catch (err) {
      console.error("Dashboard load failed", err);
    }

    setLoading(false);
  };

if (loading || !data) {
  return (
    <div className="d-flex flex-column justify-content-center align-items-center py-5">
      <div className="mb-3">
        <i
          className="bi bi-cash-stack text-success"
          style={{ fontSize: "3rem", animation: "pulse 1.5s infinite" }}
        ></i>
      </div>

      <div className="fw-bold" style={{ fontSize: "1.2rem" }}>
        Preparing Payroll Dashboard...
      </div>

      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
        Calculating earnings, deductions & summaries...
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.5; transform: scale(0.9); }
            50% { opacity: 1; transform: scale(1.1); }
            100% { opacity: 0.5; transform: scale(0.9); }
          }
        `}
      </style>
    </div>
  );
}


  const { totals, stats, recent_runs, pending_approvals } = data;

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="container py-4">

        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold">Dashboard</h3>
          <button className="btn btn-light border rounded-pill px-4 py-2">
            Back to Home
          </button>
        </div>

        {/* TOP CARDS */}
        <div className="row g-4 mb-4">

          <DashboardCard
            title="Total Payroll Cost"
            value={formatINR(totals.gross)}
            change="+2.5% from last month"
            icon="bi-cash-coin"
          />

          <DashboardCard
            title="Active Loans"
            value={stats.active_loans}
            change="-10% from last month"
            icon="bi-bank"
          />

          <DashboardCard
            title="Pending Reimbursements"
            value={stats.pending_reimbursements}
            change="+5% from last month"
            icon="bi-receipt"
          />

          <DashboardCard
            title="Total Employees"
            value={stats.total_employees}
            change="+1 from last month"
            icon="bi-people"
          />

        </div>

        {/* RECENT PAYROLL RUNS */}
        <div className="bg-white rounded-4 shadow-sm p-4 border mb-4">
          <h5 className="fw-bold mb-4">Recent Payroll Runs</h5>

          <div className="table-responsive">
            <table className="table table-borderless align-middle">
              <thead className="border-bottom">
                <tr className="text-muted">
                  <th>Period</th>
                  <th>Run Date</th>
                  <th>Status</th>
                  <th>Total Cost</th>
                  <th>Employees</th>
                </tr>
              </thead>

              <tbody>
                {recent_runs.map((run) => (
                  <tr key={run.id} className="border-bottom">
                    <td>{run.period}</td>
                    <td>{run.created_at}</td>
                    <td>
                      <StatusBadge status="Completed" />
                    </td>
                    <td>{formatINR(run.gross)}</td>
                    <td>{run.total_items}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ------------------------------------------------------------
   DASHBOARD CARD COMPONENT
-------------------------------------------------------------*/
function DashboardCard({ title, value, change, icon }) {
  return (
    <div className="col-lg-3 col-md-6">
      <div
        className="bg-white rounded-4 p-4 shadow-sm border h-100"
        style={{ minHeight: "150px" }}
      >
        <div className="d-flex justify-content-between">
          <h6 className="text-muted">{title}</h6>
          <i className={`bi ${icon} fs-4 text-primary`}></i>
        </div>

        <h3 className="fw-bold mt-2 mb-0">{value}</h3>

        <small className="text-muted">{change}</small>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------
   STATUS BADGE COMPONENT
-------------------------------------------------------------*/
function StatusBadge({ status }) {
  const map = {
    Completed: "bg-success-subtle text-success",
    Draft: "bg-secondary-subtle text-secondary",
  };

  return (
    <span
      className={`badge rounded-pill px-3 py-2 fw-semibold ${
        map[status] || "bg-warning-subtle text-warning"
      }`}
    >
      {status}
    </span>
  );
}
